import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Media, { MediaSize, isImage, isVideo, bytesToSize, getSecondsFormattedIn, getDimensionFormatted } from '../../models/Media';
import ReactPlayer from "react-player";
import { useForm } from "react-hook-form";
import { CactusLabelInput } from "../../components";
import useFormValidatorText from '../../hooks/validators/use-form-validator-text';
import MediaService from "../../services/use_case/media/MediaService";
import { useDispatch } from 'react-redux';
import * as actionCreator from "../../actions";

export type DetailLibraryInputs = {
    name: string;
    description: string;
}

interface DetailLibraryProps {
    media: Media | null;
    disableOnClose?: (value: boolean) => void;
};

const DetailLibrary: React.FC<DetailLibraryProps> = (props: DetailLibraryProps) => {

    const [t] = useTranslation();

    const [navLink, setNavLink] = useState<number>(0);
    const [isUpdate, setIsUpdate] = useState<boolean>(false);
    const [media, setMedia] = useState<Media | null>(props.media);
    const { handleSubmit, register, errors, reset } = useForm<DetailLibraryInputs>();

    const dispatch = useDispatch();
    const textValidator = register(useFormValidatorText());

    useEffect(() => {
        reset(media ?? {});
    }, [navLink]);

    useEffect(() => {
        props.disableOnClose?.(isUpdate);
    }, [isUpdate]);

    const _handleOnSubmit = async (data: DetailLibraryInputs) => {
        try {
            setIsUpdate(true);
            let updatedData = {
                name: data.name,
                description: data.description,
            }
            await new MediaService().update(media!.id, updatedData);
            let newMedia: Media = {
                ...media!,
                ...updatedData
            }
            setMedia(newMedia);
            dispatch(actionCreator.mediaUpdate(newMedia));
            setIsUpdate(false);
        }
        catch(error) {
            setIsUpdate(false);
        }
    }

    const _renderDuration = () => {

        if(!isVideo(media?.mime_type)) return null;

        return(
            <>
                <div className="col-6 text-right font-weight-bold">{t('mediaitem_duration')}:</div>
                <div className="col-6">{getSecondsFormattedIn(media?.file_duration ?? 0)}</div>
            </>
        )

    }

    const _renderDimension = () => {

        let dimensionText: string | null = getDimensionFormatted(media);

        return(
            <>
                <div className="col-6 text-right font-weight-bold">{t('mediaitem_dimension')}:</div>
                <div className="col-6">{dimensionText}</div>
            </>
        );

    }

    const _renderNavBody = () => {

        switch(navLink) {
            case 0:
                return(
                    <div className="m-3 p-3 bg-modal-box">
                        <div className="row">
                            <div className="col-6 text-right font-weight-bold">{t('mediaitem_size')}:</div>
                            <div className="col-6">{bytesToSize(media?.size)}</div>
            
                            {_renderDuration()}
                            {_renderDimension()}
                        </div>
                    </div>
                );
            case 1:
                return(
                    <form className="m-3" onSubmit={handleSubmit(_handleOnSubmit)}>
                        
                        <CactusLabelInput
                            labelTitle={t('Name')}
                            inputAttributes={{
                                id: "name",
                                name: "name",
                                type:  "text",
                                ref: textValidator,
                            }}
                            errorMessage={errors.name ? errors.name.message : null}
                        />

                        <CactusLabelInput
                            labelTitle={t('Description')}
                            inputAttributes={{
                                id: "description",
                                name: "description",
                                type:  "text",
                                ref: textValidator,
                            }}
                            errorMessage={errors.description ? errors.description.message : null}
                        />

                        <button 
                            type="submit"
                            className="btn btn-cactus mt-2"
                            disabled={isUpdate}
                        >
                            {t('Update Model', { model: t('Media') })}
                        </button>

                    </form>
                );
        }

        return null;

    }

    return(
        <div className="row">
            
            <div className="col-12 col-md-8">

                <div className="p-4">
                    {isImage(media?.mime_type) ? <img className="img-fluid w-100" src={`${media?.full_path}&size=${MediaSize.large}`} alt={media?.name} /> : null}
                    {isVideo(media?.mime_type) ? <ReactPlayer url={`${media?.full_path}&size=${MediaSize.large}`} width='100%' controls /> : null}
                </div>

            </div>

            <div className="col-12 col-md-4 px-0" style={{ borderLeft: "1px solid #0a0a0a" }}>
                
                <ul className="nav nav-tabs">
                    <li className="nav-item">
                        <a 
                            className={`nav-link ${navLink === 0 ? "active" : null}`}
                            href="!#"
                            onClick={(e) => {
                                e.preventDefault();
                                if(!isUpdate) setNavLink(0);
                            }}
                        >
                            {t('Details')}
                        </a>
                    </li>
                    <li className="nav-item">
                        <a 
                            className={`nav-link ${navLink === 1 ? "active" : null}`}
                            href="!#"
                            onClick={(e) => {
                                e.preventDefault();
                                if(!isUpdate) setNavLink(1);
                            }}
                        >
                            {t('Update')}
                        </a>
                    </li>
                </ul>

                {_renderNavBody()}

            </div>

        </div>
    );

};

export default React.memo(DetailLibrary);
